import { Texture } from "three/webgpu";
import type { GpuContext } from "./GpuContext.js";
import { configureTexture, type DrawableTexture } from "./texture.js";

/** Anything the browser can decode straight into a GPU texture. */
export type ImageSource = ImageBitmap | HTMLCanvasElement | OffscreenCanvas | HTMLImageElement;

/**
 * A GPU-resident image. Unlike glyphs, images are few and large, so each gets
 * its own texture instead of a slot in a shared atlas — no allocator, no
 * eviction, and one extra draw call at most per image on screen.
 */
export class WebwinImage implements DrawableTexture {
  readonly width: number;
  readonly height: number;
  readonly texture: Texture;
  #destroyed = false;

  private constructor(texture: Texture, width: number, height: number) {
    this.texture = texture;
    this.width = width;
    this.height = height;
  }

  static fromSource(gpu: GpuContext, source: ImageSource, label = "webwin.image"): WebwinImage {
    const width = "naturalWidth" in source ? source.naturalWidth : source.width;
    const height = "naturalHeight" in source ? source.naturalHeight : source.height;
    if (width <= 0 || height <= 0) {
      throw new Error("webwin: cannot create an image from a zero-sized source.");
    }
    // A canvas is copied now: the caller may draw into it again, and the picture is what it held when made.
    let image: TexImageSource | OffscreenCanvas = source;
    if (!(source instanceof ImageBitmap) && !(typeof HTMLImageElement !== "undefined" && source instanceof HTMLImageElement)) {
      const copy = new OffscreenCanvas(width, height);
      copy.getContext("2d")!.drawImage(source, 0, 0);
      image = copy;
    }
    const texture = configureTexture(new Texture(image as HTMLImageElement), true, "clamp", false, gpu.maxAnisotropy);
    texture.name = label;
    texture.needsUpdate = true;
    return new WebwinImage(texture, width, height);
  }

  static async fromUrl(gpu: GpuContext, url: string): Promise<WebwinImage> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`webwin: failed to load image "${url}" (${response.status})`);
    const bitmap = await createImageBitmap(await response.blob());
    // The bitmap is the texture's image, so it lives as long as the texture does.
    return WebwinImage.fromSource(gpu, bitmap, `webwin.image(${url})`);
  }

  get destroyed(): boolean {
    return this.#destroyed;
  }

  destroy(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.texture.dispose();
    const image = this.texture.image as { close?: () => void } | null;
    image?.close?.();
  }
}

/**
 * Caches images by URL so the same picture used in several controls is
 * decoded and uploaded once.
 */
export class ImageStore {
  #gpu: GpuContext;
  #byUrl = new Map<string, Promise<WebwinImage>>();

  constructor(gpu: GpuContext) {
    this.#gpu = gpu;
  }

  load(url: string): Promise<WebwinImage> {
    let pending = this.#byUrl.get(url);
    if (!pending) {
      pending = WebwinImage.fromUrl(this.#gpu, url);
      // A failed load must not poison the cache; a later retry should re-fetch.
      pending.catch(() => this.#byUrl.delete(url));
      this.#byUrl.set(url, pending);
    }
    return pending;
  }

  /** Wrap an already-decoded source; not cached, the caller owns it. */
  create(source: ImageSource, label?: string): WebwinImage {
    return WebwinImage.fromSource(this.#gpu, source, label);
  }

  async release(url: string): Promise<void> {
    const pending = this.#byUrl.get(url);
    if (!pending) return;
    this.#byUrl.delete(url);
    try {
      (await pending).destroy();
    } catch {
      // Nothing to release if the load never succeeded.
    }
  }

  clear(): void {
    for (const url of [...this.#byUrl.keys()]) void this.release(url);
  }
}
